import type { Product } from '../../catalog/domain-api'
import type { ProductSearchRow } from '../../catalog/domain-api';
import type { CartEvaluationResult } from '../../promotions/domain/promotionTypes';
import type { CartItem } from '../store/usePosStore';
import type { PosDraftPayment } from '../domain/posTypes';
import { computePosCheckoutTotals } from './posCheckoutTotals';

export const derivePosCheckoutTotals = (
  cart: CartItem[],
  paymentDrafts: PosDraftPayment[],
  evaluation?: CartEvaluationResult | null,
  extraDiscountTotal = 0,
) => {
  const base = evaluation
    ? (() => {
        const evaluatedByLineId = new Map(evaluation.lines.map((line) => [line.lineId, line]));
        return computePosCheckoutTotals(
          cart.map((item) => {
            const evaluated = evaluatedByLineId.get(item.lineId);
            if (!evaluated) {
              return { price: item.price, quantity: item.quantity };
            }
            return {
              price: evaluated.unitPriceOriginal,
              quantity: evaluated.quantity,
              effectiveUnitPrice: evaluated.unitPrice,
              lineDiscountTotal: evaluated.lineDiscountTotal,
            };
          }),
          paymentDrafts,
        );
      })()
    : computePosCheckoutTotals(
        cart.map((item) => ({ price: item.price, quantity: item.quantity })),
        paymentDrafts,
      );
  const extra = Math.max(0, Number(extraDiscountTotal) || 0);
  if (extra <= 0) return base;
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const total = Math.max(0, base.total - extra);
  const discountTotal = base.discountTotal + extra;
  const totalIngresado = base.totalIngresado;
  return {
    ...base,
    discountTotal,
    total,
    vuelto: round2(Math.max(totalIngresado - total, 0)),
    saldoPendiente: round2(Math.max(total - totalIngresado, 0)),
  };
};

type PosStockProduct = ProductSearchRow | Product;

/** Cantidad vendible en POS: combos usan BOM (comboSellableQty), no stock del padre phantom. */
export function effectivePosSellableQuantity(product: PosStockProduct): number | null {
  if (product.itemType === 'service' || product.isMisc === true) return null;
  const isCombo = 'isCombo' in product && product.isCombo === true;
  if (isCombo) {
    const sell =
      'comboSellableQty' in product ? product.comboSellableQty : undefined;
    return typeof sell === 'number' ? sell : null;
  }
  const stockQuantity = 'stockQuantity' in product ? product.stockQuantity : undefined;
  return typeof stockQuantity === 'number' ? stockQuantity : null;
}

/**
 * Productos del catálogo en grilla incluyen columnas de stock; el lookup por código también las trae cuando hay sucursal.
 * Combos: misma regla que tienda online / catálogo — vendible = mínimo de componentes (`comboSellableQty`).
 */
export const isProductCardOutOfStock = (product: PosStockProduct): boolean => {
  if (product.itemType === 'service' || product.isMisc === true) return false;
  const isCombo = 'isCombo' in product && product.isCombo === true;
  if (isCombo) {
    const sell = effectivePosSellableQuantity(product);
    if (sell == null) return false;
    return sell <= 0;
  }
  const stockId = 'stockId' in product ? product.stockId : undefined;
  const stockQuantity = 'stockQuantity' in product ? product.stockQuantity : undefined;
  return typeof stockQuantity !== 'number' || stockId == null || stockQuantity <= 0;
};

/** Códigos largos (EAN) en tarjeta POS: últimos dígitos para no saturar la vista. */
export const formatSkuForPosCard = (sku: string | null | undefined): string => {
  const t = String(sku ?? '').trim();
  if (!t) return 'Sin código';
  const digitsOnly = t.replace(/\D/g, '');
  if (digitsOnly.length >= 8) return `···${digitsOnly.slice(-4)}`;
  if (t.length > 14) return `···${t.slice(-4)}`;
  return t;
};
